/**
 * The numbers a box plot draws.
 *
 * The quartiles come from `fiveNumberSummary`, so a box plot and the quartile calculator
 * agree for every quartile method. The whiskers follow Tukey: each runs to the most
 * extreme data point that is still inside its fence (Q1 − 1.5 × IQR, Q3 + 1.5 × IQR),
 * and every point beyond a fence is drawn on its own as an outlier.
 */

import {
  type FiveNumberSummary,
  fiveNumberSummary,
  mean,
  type QuantileMethod,
} from "@/lib/stats/descriptive";

export interface BoxStats extends FiveNumberSummary {
  n: number;
  mean: number;
  /** End of the lower whisker: the smallest value at or above the lower fence. */
  whiskerLow: number;
  /** End of the upper whisker: the largest value at or below the upper fence. */
  whiskerHigh: number;
}

export function boxStats(
  values: readonly number[],
  method: QuantileMethod
): BoxStats | null {
  if (values.length === 0) {
    return null;
  }
  const summary = fiveNumberSummary(values, method);
  let whiskerLow = summary.q1;
  let whiskerHigh = summary.q3;
  for (const value of values) {
    if (value >= summary.lowerFence && value < whiskerLow) {
      whiskerLow = value;
    }
    if (value <= summary.upperFence && value > whiskerHigh) {
      whiskerHigh = value;
    }
  }
  return {
    ...summary,
    n: values.length,
    mean: mean(values),
    whiskerLow,
    whiskerHigh,
  };
}
